"use client";

import React, { useState, useEffect, useRef, useCallback } from "react";
import { ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";

/**
 * ImageSlider (Bootstrap-5-Version)
 * Automatisch durchlaufender, endlos wiederholender Bilder-Slider mit
 * optionaler Bildunterschrift. Nutzt Bootstrap-5-Klassen für Layout/
 * Positionierung statt Tailwind. Voraussetzung: bootstrap.min.css ist
 * im Projekt eingebunden (z. B. in app/layout.js: import "bootstrap/dist/css/bootstrap.min.css").
 *
 * Props:
 *  - images: [{ src, alt, caption }]
 *  - intervalMs: Zeit pro Bild in ms (Standard 4500)
 */
const defaultImages = [
  {
    src: "../vendure1.png?w=1200&q=80",
    alt: "Verlauf: aktuelle Bestellungen",
    caption: "Verlauf: aktuelle Bestellungen",
  },
  {
    src: "../vendure2.png?w=1200&q=80",
    alt: "Artikelverwaltung",
    caption: "Artikelverwaltung",
  },
  {
    src: "../vendure3.png?w=1200&q=80",
    alt: "Artikeldetails",
    caption: "Artikeldetails",
  },
  {
    src: "../vendure4.png?w=1200&q=80",
    alt: "Artikelvarianten",
    caption: "Artikelvarianten",
  },
  {
    src: "../vendure5.png?w=1200&q=80",
    alt: "Kategorien",
    caption: "Kategorien",
  },
];

const navBtnStyle = {
  width: 36,
  height: 36,
  background: "rgba(0,0,0,0.45)",
  border: "none",
};

export default function ImageSlider({ images = defaultImages, intervalMs = 4500 }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = images.length;
  const timerRef = useRef(null);

  const goTo = useCallback((i) => setIndex(((i % count) + count) % count), [count]);
  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const prev = useCallback(() => goTo(index - 1), [goTo, index]);

  useEffect(() => {
    if (paused || count <= 1) return;
    timerRef.current = setTimeout(next, intervalMs);
    return () => clearTimeout(timerRef.current);
  }, [index, paused, next, intervalMs, count]);

  return (
    <div
      className="position-relative mx-auto"
      style={{ maxWidth: 860 }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      {/* Bootstrap "ratio" hält das Seitenverhältnis, unabhängig von Bildgröße */}
      <div className="ratio ratio-16x9 rounded overflow-hidden bg-dark">
        {images.map((img, i) => (
          <div
            key={i}
            className="position-absolute top-0 start-0 w-100 h-100"
            style={{
              opacity: i === index ? 1 : 0,
              transition: "opacity 700ms ease",
              pointerEvents: i === index ? "auto" : "none",
            }}
            aria-hidden={i !== index}
          >
            <img
              src={img.src}
              alt={img.alt || ""}
              className="w-100 h-100"
              style={{ objectFit: "cover" }}
              draggable={false}
            />
            {img.caption && (
              <div
                className="position-absolute bottom-0 start-0 end-0 text-white px-3 pb-2"
                style={{
                  background:
                    "linear-gradient(to top, rgba(0,0,0,.8), rgba(0,0,0,.3) 65%, transparent)",
                  paddingTop: "2.5rem",
                }}
              >
                <p className="mb-0 small">{img.caption}</p>
              </div>
            )}
          </div>
        ))}

        {/* Pfeile */}
        <button
          onClick={prev}
          aria-label="Vorheriges Bild"
          className="btn rounded-circle position-absolute top-50 start-0 translate-middle-y ms-2 d-flex align-items-center justify-content-center text-white p-0"
          style={navBtnStyle}
        >
          <ChevronLeft size={20} />
        </button>
        <button
          onClick={next}
          aria-label="Nächstes Bild"
          className="btn rounded-circle position-absolute top-50 end-0 translate-middle-y me-2 d-flex align-items-center justify-content-center text-white p-0"
          style={navBtnStyle}
        >
          <ChevronRight size={20} />
        </button>

        {/* Play/Pause */}
        <button
          onClick={() => setPaused((p) => !p)}
          aria-label={paused ? "Wiedergabe fortsetzen" : "Wiedergabe pausieren"}
          className="btn rounded-circle position-absolute top-0 end-0 mt-2 me-2 d-flex align-items-center justify-content-center text-white p-0"
          style={{ ...navBtnStyle, width: 30, height: 30 }}
        >
          {paused ? <Play size={14} /> : <Pause size={14} />}
        </button>
      </div>

      {/* Punkt-Navigation */}
      <div className="d-flex justify-content-center gap-2 mt-2">
        {images.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            aria-label={`Zu Bild ${i + 1} springen`}
            aria-current={i === index}
            className="border-0 p-0 rounded-pill"
            style={{
              width: i === index ? 24 : 6,
              height: 6,
              background: i === index ? "#212529" : "#ced4da",
              transition: "width 300ms ease",
            }}
          />
        ))}
      </div>
    </div>
  );
}
